import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../hooks/useNotification';
import LoadingSpinner from '../components/LoadingSpinner';
import axios from 'axios';

const LoginScreen: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [systemStatus, setSystemStatus] = useState<'checking' | 'operational' | 'unavailable'>('checking');
  const { login } = useAuth();
  const { addNotification } = useNotification();

  // Check backend health on mount
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const response = await axios.get('/api/health', { timeout: 5000 });
        if (response.data?.status === 'healthy') {
          setSystemStatus('operational');
        } else {
          setSystemStatus('unavailable');
        }
      } catch (error) {
        setSystemStatus('unavailable');
      }
    };

    checkHealth();
    // Recheck every 30 seconds
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login({ username, password });
      addNotification({
        type: 'success',
        title: 'Login Successful',
        message: 'Welcome to TEAAM EMS Dispatch',
      });
    } catch (error: any) {
      // Different error messages based on type
      let errorTitle = 'Login Failed';
      let errorMessage = error.message || 'An error occurred during login';
      
      if (error.message?.includes('Unable to connect')) {
        errorTitle = 'Connection Error';
        errorMessage = 'Cannot reach the server. Please ensure the backend is running.';
      } else if (error.message?.includes('Invalid credentials')) {
        errorMessage = 'Please check your username and password.';
      }
      
      addNotification({
        type: 'error',
        title: errorTitle,
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* TEAAM Logo */}
        <div className="flex justify-center">
          <div className="bg-teaam-blue rounded-lg p-6 shadow-lg">
            <h1 className="text-3xl font-bold text-white">TEAAM EMS</h1>
          </div>
        </div>
        
        <h2 className="mt-8 text-center text-3xl font-bold text-gray-900">
          Sign in to your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Helicopter Emergency Medical Services
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="username" className="form-label">
                Username
              </label>
              <div className="mt-1">
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  autoComplete="username"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className="form-input"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  className="form-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full flex justify-center py-3 px-4 text-sm font-medium focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="small" className="mr-2" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  British Columbia, Canada
                </span>
              </div>
            </div>
          </div>

          {/* System Status Indicator */}
          <div className="mt-4 flex items-center justify-center">
            <div className="flex items-center gap-2 text-xs">
              {systemStatus === 'checking' && (
                <>
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" />
                  <span className="text-gray-500">Checking system status...</span>
                </>
              )}
              {systemStatus === 'operational' && (
                <>
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-gray-600">System Operational</span>
                </>
              )}
              {systemStatus === 'unavailable' && (
                <>
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-red-600">System Unavailable</span>
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LoginScreen;